package dev.lingvo.site.components.editor

import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderLeft
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.borderRight
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.noTransition
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.toSitePalette
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

val KeyPaneStyle = CssStyle.base {
    Modifier
        .height(100.percent)
        .minHeight(0.px)
        .backgroundColor(colorMode.toSitePalette().surface)
        .borderRight(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("overflow", "hidden")
            // This element's own sizing as a child of the workspace row, unrelated to the
            // `flex-direction: column` above (that governs its header-over-list children). Only
            // meaningful in `EditorWorkbenchTrioStyle`, now a flex row alongside the sidecar's
            // animated width — see the comment there. `min-width: 0` is not needed alongside it:
            // `flex-shrink: 0` already holds this pane at its fixed basis.
            property("flex", "0 0 ${EDITOR_QUEUE_WIDTH_PX}px")
        }
}

val KeyScrollerStyle = CssStyle.base {
    Modifier
        .flexGrow(1)
        .minHeight(0.px)
        .styleModifier {
            property("height", "auto")
            property("overflow-y", "auto")
            property("overflow-x", "hidden")
            property("overscroll-behavior", "contain")
            property("scrollbar-gutter", "stable")
            // Without this, flex-basis defaults to the `height: auto` above, which for a scroll
            // container means its *content* height — the full virtualized list, 25,600px at 400
            // rows. A flex sibling negotiates shrinkage against that number, not against the
            // viewport, so `flex-basis: 0` is what makes "grow to fill the pane" actually mean the
            // pane rather than the list. See EditorQueueFilterSurfaceStyle for the sibling this
            // used to crush.
            property("flex-basis", "0")
        }
}

val EditorQueueEssentialBoundaryStyle = CssStyle.base {
    Modifier
        .flexGrow(1)
        .minWidth(0.px)
        .color(colorMode.toSitePalette().textMuted)
}

/**
 * The filter panel, and the other half of the crush documented on [CompactQueueHeaderStyle].
 *
 * `flex: 0 0 auto` on purpose: this panel must neither grow into a short list's empty space nor
 * shrink under a long one's. It used to be `flex-grow: 1` with `min-height: 0`, next to
 * [KeyScrollerStyle] — also `flex-grow: 1` with `min-height: 0` — whose flex basis was its full
 * *content* height, 25,600px at 400 rows. Flex shrinkage is distributed by scaled shrink factor
 * against that deficit, so this panel's ~250 natural pixels shrank to roughly 8.
 */
/**
 * The filter surface, and the three states it moves through.
 *
 * **It used to snap.** The panel was mounted by a bare `if` with no presence lifecycle, so it
 * existed on one frame and did not on the one before — a CSS transition needs two computed states
 * and a conditionally-mounted element only ever has one.
 *
 * Opacity and a short travel, and deliberately not height. The panel is an in-flow sibling above
 * the key list, so animating its height would animate the list's position, and the motion contract
 * is explicit that editor queue measurements never animate. The layout settles at once — truthful,
 * and the rows do not slide under a reader's eye — while the panel's own content arrives.
 */
val EditorQueueFilterSurfaceStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .backgroundColor(colorMode.toSitePalette().surfaceSunken)
            .color(colorMode.toSitePalette().textPrimary)
            .padding(Space.LG)
            .gap(Space.MD)
            .styleModifier {
                property("box-sizing", "border-box")
                property("flex", "0 0 auto")
                property(
                    "transition",
                    "opacity ${Motion.StandardMs}ms ${Motion.EaseOut}, " +
                        "transform ${Motion.StandardMs}ms ${Motion.EaseOut}",
                )
            }
    }
    // The state it is in one frame before it is asked to arrive. Without a starting value there is
    // nothing to transition *from* — the same asymmetry that made `LModal`'s scrim appear at full
    // opacity for a frame.
    cssRule("[data-presence=\"entering\"]") {
        Modifier.styleModifier {
            property("opacity", "0")
            property("transform", "translateY(-${Motion.PopoverTravelPx}px)")
        }
    }
    cssRule("[data-presence=\"exiting\"]") {
        Modifier.styleModifier {
            property("opacity", "0")
            property("transform", "translateY(-${Motion.PopoverTravelPx}px)")
            // A panel on its way out still covers the controls it is uncovering.
            property("pointer-events", "none")
            property(
                "transition",
                "opacity ${Motion.FastMs}ms ${Motion.EaseIn}, " +
                    "transform ${Motion.FastMs}ms ${Motion.EaseIn}",
            )
        }
    }
}

/**
 * The ground the filter panel stands on when it is the whole pane.
 *
 * On the compact layout `EditorQueueModeBody` swaps the key list out for the filter surface, and
 * that surface is deliberately `flex: 0 0 auto` — see [EditorQueueFilterSurfaceStyle] for the
 * crush it is protecting itself from. In FILTERS mode there is nothing below it to fill the rest
 * of the pane, so at 390 the panel stopped around 430px down an 844px viewport and left ~450px of
 * bare `surface` beneath it, with a hard two-tone seam across the phone: a page that had failed to
 * render, under a sentence saying 45 keys match. This wrapper takes the leftover height and paints
 * it the panel's own `surfaceSunken`, so the dead zone belongs to the panel. The surface inside
 * keeps its `0 0 auto` and is not stretched.
 */
val EditorQueueFilterModeStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .backgroundColor(colorMode.toSitePalette().surfaceSunken)
        .styleModifier {
            property("box-sizing", "border-box")
            property("display", "flex")
            property("flex-direction", "column")
            property("flex", "1 1 auto")
            property("min-height", "0")
            property("overflow-y", "auto")
        }
}

val EditorQueueFilterHeadingStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textPrimary)
        .displayFont()
        // HeadingSmall, matching LModal's own H2-plus-close-button header row (LModal.kt) — this
        // "Filters" H2 sits in exactly that shape, title beside a couple of buttons, so it follows
        // that precedent rather than staying flat with the body text below it. Bold stays too,
        // for the same reason LModal's does.
        .fontSize(TypeScale.HeadingSmall)
        .fontWeight(700)
        .gap(Space.SM)
        .styleModifier {
            property("margin", "0")
            // The heading carries the filter glyph beside its word, so it is a flex line rather
            // than a text node. `SM`, the distance `LButton` puts between a leading glyph and its
            // label, so icon-beside-label is one value wherever it appears.
            property("display", "inline-flex")
            property("align-items", "center")
        }
}

val EditorQueueFilterControlStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .color(colorMode.toSitePalette().textMuted)
        .styleModifier {
            property("background-color", colorMode.toSitePalette().surfaceSunken.toString())
        }
}

/**
 * The queue header, which must not shrink — and used to.
 *
 * It sits in a flex column above [KeyScrollerStyle], which grows. With `min-height: 0` and the
 * default `flex-shrink: 1`, the moment the key list was taller than the pane — which is every real
 * project — the scroller won the negotiation and crushed the header from its natural 69px to 28px.
 * Two things broke at once, and both were invisible as *layout* problems:
 *
 *  - **The progress line collapsed to zero pixels** and painted behind the first row, so
 *    "36 of 45 translated · 9 remaining" — the count PRODUCT.md's fourth principle exists to
 *    protect — was never seen by anybody with more keys than fit on screen.
 *  - **The 40px search field overflowed its own 28px box** and, because this is `position: sticky`
 *    with `z-index: 20` and an opaque background, painted across the top of the first key row.
 *    Measured: field 56→96, first row starting at 80 with its label at 94.
 *
 * `flex-shrink: 0` is the whole fix. The natural height was always correct; nothing was allowed to
 * keep it. `max-height` stays, because the filter panel expands into this box.
 *
 * The cap was 108 when the header held one line of prose. It now holds two stat tiles, the queue's
 * automations, the segmented bar and — while a run is going — a progress readout, which is 176 at
 * its tallest. Raised rather than removed: the queue's rows are what this pane is for, and a header
 * free to grow is one that eats them. `estimatedVisibleQueueRows` reads the same number, so the two
 * cannot drift.
 */
val CompactQueueHeaderStyle = CssStyle.base {
    Modifier
        .position(Position.Sticky)
        .top(0.px)
        .zIndex(20)
        .width(100.percent)
        .backgroundColor(colorMode.toSitePalette().surface)
        .borderBottom(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
        .padding(top = Space.XS, right = Space.SM, bottom = Space.XS, left = Space.SM)
        .gap(Space.XS)
        .styleModifier {
            property("box-sizing", "border-box")
            property("flex-shrink", "0")
            property("max-height", "${EDITOR_QUEUE_HEADER_MAX_HEIGHT_PX}px")
        }
}

val CompactQueueProgressStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .color(colorMode.toSitePalette().textSecondary)
        .fontSize(TypeScale.Small)
        .lineHeight(1.2)
        .tabularNumbers()
        .styleModifier {
            property("white-space", "nowrap")
            property("overflow", "hidden")
            property("text-overflow", "ellipsis")
        }
}

val CompactQueueChipsStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .height(32.px)
        .minHeight(32.px)
        .gap(Space.XS)
        .styleModifier {
            property("display", "flex")
            property("flex-wrap", "nowrap")
            property("overflow-x", "auto")
            property("overflow-y", "hidden")
            property("scrollbar-width", "none")
        }
}

val KeyRowStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .height(EDITOR_ROW_HEIGHT_PX.px)
            .minHeight(EDITOR_ROW_HEIGHT_PX.px)
            .backgroundColor(Colors.Transparent)
            .color(colorMode.toSitePalette().textPrimary)
            // Reserved, never drawn here: EMPTY gets nothing, not a grey rail, because a rail on
            // every row is a rail that means nothing. The row paints it when it has a state to say.
            .borderLeft(STATE_RAIL_PX.px, LineStyle.Solid, Colors.Transparent)
            .borderBottom(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .padding(top = Space.SM, right = Space.SM, bottom = Space.SM, left = Space.SM)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("box-sizing", "border-box")
                property("display", "flex")
                property("flex-direction", "column")
                property("justify-content", "center")
                property("appearance", "none")
                property("border-top", "0")
                property("border-right", "0")
                property("font", "inherit")
                property("text-align", "start")
                property("overflow", "hidden")
            }
            .noTransition()
    }
    hover {
        Modifier.backgroundColor(colorMode.toSitePalette().surfaceRaised)
    }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "-3px") }
    }
}

/**
 * The mono key name, truncated at the **head** rather than the tail.
 *
 * Two keys that share a long prefix differ only in what comes after it —
 * `settings.billing.renewal_notice` vs `…renewal_failed` — and a plain end-ellipsis clips exactly
 * the half that tells them apart. `direction: rtl` moves the browser's own overflow math to the
 * other edge, so the ellipsis lands on the shared prefix and the discriminating tail survives;
 * `unicode-bidi: plaintext` keeps the identifier itself reading left-to-right, since it resolves
 * direction per run from its own first strong character rather than the box's. `text-align: left`
 * only matters when the name is short enough not to truncate, so it stays flush left instead of
 * following `rtl`'s default right alignment.
 */
val KeyNameStyle = CssStyle.base {
    Modifier
        .minWidth(0.px)
        .monoFont()
        .fontSize(TypeScale.Small)
        .fontWeight(500)
        .lineHeight(1.0)
        .color(colorMode.toSitePalette().textSecondary)
        .styleModifier {
            property("overflow", "hidden")
            property("text-overflow", "ellipsis")
            property("white-space", "nowrap")
            // **These two are physical on purpose and must stay that way.** `direction: rtl`
            // flips which end of an overlong path gets ellipsised, so the reader sees the file
            // name rather than the repository root; `text-align: left` then puts the visible end
            // back where they expect it. Rewriting either to a logical keyword undoes the trick.
            property("direction", "rtl")
            property("text-align", "left")
            property("unicode-bidi", "plaintext")
        }
}

val KeyTargetPreviewStyle = CssStyle.base {
    Modifier
        .minWidth(0.px)
        .fontSize(TypeScale.Micro)
        .color(colorMode.toSitePalette().textSecondary)
        .styleModifier {
            property("flex", "0 1 30%")
            property("max-width", "30%")
            property("overflow", "hidden")
            property("text-overflow", "ellipsis")
            property("white-space", "nowrap")
        }
}

val KeyQaStyle = CssStyle.base {
    Modifier
        .fontSize(TypeScale.Micro)
        .fontWeight(600)
        .styleModifier {
            property("display", "inline-flex")
            property("flex", "0 0 auto")
            property("align-items", "center")
            property("gap", "3px")
            property("white-space", "nowrap")
        }
}

/**
 * The severity dot: 8px, round, and the only thing on a queue row that is allowed to be a colour.
 *
 * Sized to be seen at a glance down a column of sixty-four-pixel rows without competing with the
 * key name beside it. `flex-shrink: 0` because it is the one element in the row that must never
 * give up width — a dot that shrinks is a dot that disappears.
 */
val KeyQaDotStyle = CssStyle.base {
    Modifier
        .width(8.px)
        .height(8.px)
        .borderRadius(Radius.Pill)
        .styleModifier {
            property("display", "inline-block")
            property("flex-shrink", "0")
        }
}

/**
 * The clamp itself is correct: two lines, `overflow: hidden`, and `scrollHeight > clientHeight` on
 * a longer source is the definition of a working clamp, not a defect. `line-height: 1.4` is the
 * fix here — at 1.0 the line box was shorter than the font's own ascent+descent in every script this
 * pane carries, so the two clamped lines collided into each other. Raised together with
 * [EDITOR_ROW_HEIGHT_PX]: the row has no slack for a taller line box without it.
 */
val KeySourceStyle = CssStyle.base {
    Modifier
        .minWidth(0.px)
        .color(colorMode.toSitePalette().textPrimary)
        .fontSize(TypeScale.BodySmall)
        .lineHeight(1.4)
        .styleModifier {
            property("display", "-webkit-box")
            property("-webkit-box-orient", "vertical")
            property("-webkit-line-clamp", "2")
            property("overflow", "hidden")
            property("overflow-wrap", "anywhere")
            property("white-space", "normal")
        }
}

val KeyMachineLabelStyle = CssStyle.base {
    Modifier
        .monoFont()
        .fontSize(TypeScale.Micro)
        .fontWeight(600)
        .color(colorMode.toSitePalette().textMuted)
        .styleModifier {
            property("letter-spacing", "0")
            property("text-transform", "uppercase")
        }
}
